import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Button from 'Components/DesignSystem/Button/Button';
import Tag from 'Components/DesignSystem/Tag/Tag';
import Typography from 'Components/DesignSystem/Typography/Typography';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { loadAttributes } from '../ModelServices/ModalServices';
import { AttributeDataType, AttributeSelectionDialogProps as AttributeSelectionDialogProps } from '../model';

const COMPONENT_ID = 'attribute-selector-dialog';
const ATTRIBUTE_LIMIT = 10;
const ALLOWED_ATTRIBUTE_DATA_TYPES = ["TEXT", "INTEGER", "DECIMAL"]

type TAttributeOption = {
    id: string;
    name: string;
    datatype: AttributeDataType;
};

export const AttributeSelectionDialog = ({
    isOpen,
    onClose,
    title,
    categoryId,
    onSave,
    onCancel,
    saveButtonText = 'Save',
    cancelButtonText = 'Cancel',
    isLoading = false,
    existingAttributeIds
}: AttributeSelectionDialogProps) => {
    const [attributes, setAttributes] = useState<TAttributeOption[]>([]);
    const [selectedAttribute, setSelectedAttribute] = useState<TAttributeOption | null>(null);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingAttributes, setLoadingAttributes] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const hasMore = attributes.length < totalCount;

    const filteredAttributes = useMemo(() => {
        if (!existingAttributeIds || existingAttributeIds.length === 0) {
            return attributes;
        }
        return attributes.filter((attr) => !existingAttributeIds.includes(attr.id));
    }, [attributes, existingAttributeIds]);

    const fetchAttributeData = useCallback(
        async (pageNum: number, search: string, reset: boolean = false) => {
            if (!categoryId) return;

            setLoadingAttributes(true);

            try {
                const response = await loadAttributes([categoryId], ALLOWED_ATTRIBUTE_DATA_TYPES, pageNum, ATTRIBUTE_LIMIT, search);
                const errorFound = response[0];
                const data = response[1]?.data?.[0];

                if (!errorFound && data) {
                    const newAttributes: TAttributeOption[] = (data.attributes ?? []).map(
                        (attr: { id: string; name: string; datatype: string }) => ({
                            id: attr.id,
                            name: attr.name,
                            datatype: attr.datatype as AttributeDataType,
                        })
                    );

                    setTotalCount(data.total_count ?? 0);
                    setAttributes((prev) => (reset ? newAttributes : [...prev, ...newAttributes]));
                }
            } catch (error) {
                console.error('Failed to fetch attributes:', error);
            } finally {
                setLoadingAttributes(false);
            }
        },
        [categoryId]
    );

    useEffect(() => {
        if (isOpen && categoryId) {
            setAttributes([]);
            setPage(1);
            setSearchQuery('');
            setSelectedAttribute(null);
            fetchAttributeData(1, '', true);
        }
    }, [isOpen, categoryId, fetchAttributeData]);

    /*
        The Dropdown owns open/close, outside-click and the near-bottom detection now;
        what stays here is the page cursor and the "is there more" guard, which are
        this component's business and not the control's.
    */
    const handleScrollEnd = useCallback(() => {
        if (loadingAttributes || !hasMore) return;
        const nextPage = page + 1;
        setPage(nextPage);
        fetchAttributeData(nextPage, searchQuery, false);
    }, [loadingAttributes, hasMore, page, searchQuery, fetchAttributeData]);

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setAttributes([]);
            setPage(1);
            fetchAttributeData(1, value, true);
        }, 300);
    };

    const handleSelectAttribute = (attributeId: string | number) => {
        setSelectedAttribute(filteredAttributes.find((a) => a.id === attributeId) ?? null);
    };

    const handleSave = () => {
        onSave(selectedAttribute);
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            onClose();
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    const getDataTypeDisplayText = (datatype: AttributeDataType): string => {
        switch (datatype) {
            case 'TEXT':
                return 'Text';
            case 'INTEGER':
                return 'Integer';
            case 'DECIMAL':
                return 'Decimal';
            default:
                return datatype;
        }
    };

    if (!isOpen) return null;

    /*
        The SHELL is a DS Dialog; the combobox below is deliberately NOT a DS Dropdown.
        This one pages: `handleScroll` fetches the next slice of attributes as the list
        is scrolled, with its own loading row. `Dropdown`'s `searchable` filters a list
        it already has and has no lazy-loading hook, so swapping it in would quietly
        drop the paging — behaviour for consistency, the wrong trade. Its markup is on
        the semantic tokens instead, and the `role="combobox"`/`listbox` wiring it
        already had is kept.
    */
    return (
        <Dialog
            isOpen
            onClose={handleClose}
            title={title}
            data-testid={COMPONENT_ID}
            footer={(
                /* Cancel far left, the committing action right — matching
                   CreateFieldDialog and CreateISRDialog. */
                <div
                    id={`${COMPONENT_ID}-footer`}
                    data-testid={`${COMPONENT_ID}-footer`}
                    className="tw-flex tw-items-center tw-justify-between tw-gap-3"
                >
                    <Button
                        id={`${COMPONENT_ID}-cancel-btn`}
                        data-testid={`${COMPONENT_ID}-cancel-btn`}
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        {cancelButtonText}
                    </Button>
                    <Button
                        id={`${COMPONENT_ID}-save-btn`}
                        data-testid={`${COMPONENT_ID}-save-btn`}
                        variant="primary"
                        onClick={handleSave}
                        /* § 280 — `loading` is the DS Button's own busy state, and it
                           ORs into the same `isDisabled` this used to compute by hand
                           (`disabled || loading`), so the button is unavailable under
                           exactly the conditions it was before and `handleSave` is
                           guarded identically. What changes is what the user gets: the
                           label STAYS beside a themed DS Spinner instead of being
                           replaced by a raw glyph, and the button announces
                           `aria-busy` — the accessible name no longer disappears at
                           the moment the action is running. */
                        disabled={!selectedAttribute}
                        loading={isLoading}
                        className="tw-min-w-[80px]"
                    >
                        {saveButtonText}
                    </Button>
                </div>
            )}
        >
            <div
                id={`${COMPONENT_ID}-body`}
                data-testid={`${COMPONENT_ID}-body`}
                className="tw-space-y-4"
            >
                <div>
                    <FieldTitle
                        label="Attribute"
                        required
                        htmlFor={`${COMPONENT_ID}-dropdown-trigger`}
                        data-testid={`${COMPONENT_ID}-attribute-label`}
                    />

                    {/*
                        A DS Dropdown, `searchable` + `usePortal`. It was a bespoke
                        combobox: `<li onClick>` options with no `tabIndex` and no key
                        handling, so the list could not be operated by keyboard at all —
                        and once the shell became a DS Dialog its absolutely-positioned
                        menu was clipped by the dialog body's `overflow-y-auto`, which
                        `usePortal` is exactly the answer to.

                        The three props that made this possible are new
                        (design-system § 124): `onSearchChange` for server-side search,
                        `onScrollEnd` for paging, `loading` for the trailing row. The
                        300ms debounce and the page cursor stay HERE — they are this
                        screen's policy, not the control's.
                    */}
                    <Dropdown
                        id={`${COMPONENT_ID}-dropdown-trigger`}
                        data-testid={`${COMPONENT_ID}-dropdown`}
                        label=""
                        aria-label="Attribute"
                        placeholder="Select Attribute"
                        searchPlaceholder="Search"
                        searchable
                        usePortal
                        disabled={isLoading}
                        loading={loadingAttributes}
                        options={filteredAttributes.map((attribute) => ({
                            value: attribute.id,
                            label: attribute.name,
                        }))}
                        value={selectedAttribute?.id ?? ''}
                        onChange={handleSelectAttribute}
                        onSearchChange={handleSearchChange}
                        onScrollEnd={handleScrollEnd}
                        className="tw-mt-1"
                    />
                </div>

                {selectedAttribute && (
                    <div>
                        <Typography
                            variant="body-primary"
                            as="p"
                            id={`${COMPONENT_ID}-datatype-label`}
                            className="tw-text-ink tw-mb-2"
                        >
                            Data Type
                        </Typography>
                        <Tag
                            data-testid={`${COMPONENT_ID}-datatype-badge`}
                            variant="meta"
                            label={getDataTypeDisplayText(selectedAttribute.datatype)}
                        />
                    </div>
                )}
            </div>
        </Dialog>
    );
};